import React from 'react';
import PropTypes from 'prop-types';

import Flickity from 'react-flickity-component';
import 'flickity/dist/flickity.css';

const EstateTeaserLink = ({ estate, children }) => {
    if (estate.allowDetailPage) {
        return (
            <a className="estate-teaser__link" href={estate.url}>{children}</a>
        );
    } else {
        return (
            <div className="estate-teaser__content-wrapper">{children}</div>
        );
    }

    return children;
};

class EstateTeaser extends React.Component {
    componentDidMount = () => {
        if (this.props.estate.allowDetailPage) {
            // this loads all the images when they click the arrows
            this.flickity.on('change', () => {
                this.flickity.cells.forEach((cell) => {
                    cell.element.style.backgroundImage = `url('${cell.element.dataset.image}')`;
                });
            });

            // this is for the initial image.
            this.flickity.on('select', () => {
                const { selectedElement } = this.flickity;

                if (selectedElement) {
                    selectedElement.style.backgroundImage = `url('${selectedElement.dataset.image}')`;
                }
            });

            let offset = 1;
            const update = () => {
                this.flickity.resize();

                if (offset < 5) {
                    setTimeout(() => {
                        update();
                    }, 500 * offset);
                }

                offset += 1;
            };

            setTimeout(() => {
                update();
            }, 500);
        }
    };

    // Depeding on whether the estate is available or not we render Flickity.
    renderImage = () => {
        if (this.props.estate.allowDetailPage) {
            return (
                <Flickity
                    className="estate-teaser-image-carousel"
                    options={{
                        pageDots: false,
                        wrapAround: true,
                    }}
                    flickityRef={c => this.flickity = c}
                >
                    {this.props.estate.images.slice(0, window.estateTeaserMaxPreviewImages).map((image) => {
                        if (this.props.estate.allowDetailPage) {
                            return (
                                <a
                                    className="estate-teaser-image-carousel__item"
                                    href={this.props.estate.url}
                                    aria-hidden="true"
                                    data-image={image}
                                    key={image}
                                />
                            );
                        }

                        return (
                            <div
                                className="estate-teaser-image-carousel__item"
                                data-image={image}
                                key={image}
                            />
                        );
                    })}
                </Flickity>
            );
        } else {
            return (
                <div
                    className="estate-teaser-image-carousel__item"
                    style={{ backgroundImage: `url(${this.props.estate.images[0]})` }}
                />
            );
        }
    };

    render = () => {
        const { estate } = this.props;

        const onMouseEnter = () => {
            if (window.setActiveHitId) {
                window.setActiveHitId(estate.id);
            }
        };

        return (
            <div className="estate-teaser" onMouseEnter={onMouseEnter}>
                <div className="estate-teaser__inner">
                    <div className="estate-teaser__actions">
                        <div className="estate-teaser__purpose">
                            {estate.statusLabel}
                        </div>
                        {estate.projectSoldLabel && (
                            <div className="estate-teaser__sold-label">
                                {estate.projectSoldLabel}
                            </div>
                        )}
                    </div>
                    { estate.isNew && (
                        <div className="estate-teaser__new">
                            Nieuw
                        </div>
                    )}
                    <div className="estate-teaser__images-wrapper">
                        {(estate.purposeStatus == 'optie-te-koop' || estate.purposeStatus == 'optie-te-huur') && (
                            <a className="estate-teaser__in-option" href={this.props.estate.url}>
                                <svg viewBox="0 0 183 185">
                                    <use xlinkHref="#svg-lines" />
                                </svg>
                                <span className="estate-teaser__in-option__label">optie</span>
                            </a>
                        )}
                        { this.renderImage() }

                        {estate.isProject && (
                            <div className="big-project-teaser__stats">
                                {estate.projectRegistrationRights && (
                                    <div className="big-project-teaser__stats-item">
                                        <strong>{estate.projectRegistrationRights}</strong>
                                        <span>RR</span>
                                        <small>onder voorwaarden</small>
                                    </div>
                                )}
                                {estate.projectBtw && (
                                    <div className="big-project-teaser__stats-item">
                                        <strong>{estate.projectBtw}</strong>
                                        <span>BTW</span>
                                        <small>onder voorwaarden</small>
                                    </div>
                                )}
                            </div>
                        )}

                    </div>
                    <EstateTeaserLink estate={estate}>
                        <div className="estate-teaser__content">
                            <div className="estate-teaser__title">
                                <div className="estate-teaser__location">
                                    <div className="estate-teaser__logo">
                                        <svg viewBox="0 0 29 33">
                                            <use xlinkHref="#svg-mini-logo" />
                                        </svg>
                                    </div>
                                    {estate.zipCode}
                                </div>
                                <div className="estate-teaser__title">
                                    {estate.title}
                                </div>
                            </div>
                        </div>
                        <div className="estate-teaser__metadata">
                            {estate.isProject && (
                                <div className="estate-teaser__price">
                                    <small>vanaf</small> {estate.projectPrice}
                                </div>
                            )}
                            {Boolean(estate.displayPrice) && estate.allowDetailPage && (
                                <div className="estate-teaser__price">
                                    {estate.price}
                                </div>
                            )}
                            {estate.allowDetailPage && Boolean(estate.displayPrice) === false && (
                                <div className="estate-teaser__price">
                                    Prijs op aanvraag
                                </div>
                            )}
                            <div className="estate-teaser__statistics">
                                {estate.bedrooms > 0 && (
                                    <div className="estate-teaser__statistic estate-teaser__statistic--bedroom">
                                        <svg viewBox="0 0 24 16">
                                            <use xlinkHref="#svg-bedroom" />
                                        </svg>
                                        {estate.bedrooms}
                                    </div>
                                )}
                                {estate.bathrooms > 0 && (
                                    <div className="estate-teaser__statistic estate-teaser__statistic--bathroom">
                                        <svg viewBox="0 0 21 19">
                                            <use xlinkHref="#svg-bathroom" />
                                        </svg>
                                        {estate.bathrooms}
                                    </div>
                                )}
                                {estate.area > 0 && (
                                    <div className="estate-teaser__statistic estate-teaser__statistic--popp">
                                        <svg viewBox="0 0 20 20">
                                            <use xlinkHref="#svg-wopp" />
                                        </svg>
                                        {estate.area} m²
                                    </div>
                                )}
                            </div>
                        </div>
                    </EstateTeaserLink>
                </div>
            </div>
        );
    };
}

EstateTeaser.propTypes = {
    estate: PropTypes.shape({
        allowDetailPage: PropTypes.bool,
        area: PropTypes.number,
        bedrooms: PropTypes.number,
        bathrooms: PropTypes.number,
        displayCity: PropTypes.string,
        displayPrice: PropTypes.bool,
        id: PropTypes.string,
        image: PropTypes.string,
        isProject: PropTypes.bool,
        price: PropTypes.number,
        purpose: PropTypes.string,
        url: PropTypes.string.isRequired,
        subcategory: PropTypes.string,
        surface: PropTypes.number,
        title: PropTypes.string.isRequired,
        purposeStatus: PropTypes.string,
        group: PropTypes.string,
    }).isRequired,
};

export { EstateTeaser };
